import cn from 'classnames'
import { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'
import { IconButton } from '../icon-button'
import styles from './Carousel.module.css'
import { CarouselProps } from './Carousel.types'
import { EmblaCarouselContext } from './useCarouselItemsInView'

const OPTIONS: EmblaOptionsType = { dragFree: true, loop: false, startIndex: 0, align: 'start' }

export function Carousel({
  hasControls,
  children,
  prevButtonAriaLabel,
  nextButtonAriaLabel,
  className,
  isOverflowing = false,
}: CarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel(OPTIONS)
  const [lazyLoadedSlides, setLazyLoadedSlides] = useState<HTMLElement[]>([])
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const inViewHandler = useCallback(() => {
    if (!emblaApi) {
      return
    }

    const slides = emblaApi.slideNodes()
    const slidesInView = emblaApi.slidesInView()
    const items = slidesInView.map(slide => slides[slide])
    const newLazyLoadedSlides = Array.from(new Set([...lazyLoadedSlides, ...items]))

    if (slides.length === newLazyLoadedSlides.length) {
      emblaApi.off('slidesInView', inViewHandler)
    }

    setLazyLoadedSlides(newLazyLoadedSlides)
  }, [emblaApi, lazyLoadedSlides])

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi || emblaApi.slideNodes().length === lazyLoadedSlides.length) {
      return
    }

    emblaApi.on('slidesInView', inViewHandler)
    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)

    return () => {
      emblaApi.off('slidesInView', inViewHandler)
    }
  }, [emblaApi, inViewHandler, lazyLoadedSlides.length, onSelect])

  return (
    <EmblaCarouselContext value={lazyLoadedSlides}>
      <div className={cn(styles.container, { [styles.containerOverflowing]: isOverflowing }, className)}>
        <div ref={emblaRef}>
          <div className={styles.itemsWrapper}>{children}</div>
        </div>

        {hasControls && (
          <div className={styles.buttonsWrapper}>
            <IconButton
              onClick={() => emblaApi?.scrollPrev()}
              glyph="ChevronLeft"
              variant="secondary"
              size="S"
              disabled={prevBtnDisabled}
              aria-label={prevButtonAriaLabel}
            />
            <IconButton
              onClick={() => emblaApi?.scrollNext()}
              glyph="ChevronRight"
              variant="secondary"
              size="S"
              disabled={nextBtnDisabled}
              aria-label={nextButtonAriaLabel}
            />
          </div>
        )}
      </div>
    </EmblaCarouselContext>
  )
}
